import { useState, useEffect, createContext, useContext } from 'react';
import type { User } from 'firebase/auth';
import { onAuthChange, signInWithGoogle, signOut } from '../lib/firebase';
import { getOrCreateUser } from '../lib/api';
import type { TeamUser } from '../types';

interface AuthContextValue {
  firebaseUser: User | null;
  teamUser: TeamUser | null;
  loading: boolean;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  isSysadmin: boolean;
  isDev:      boolean;
  isSenior:   boolean;
  isJunior:   boolean;
}

export const AuthContext = createContext<AuthContextValue>({
  firebaseUser: null,
  teamUser: null,
  loading: true,
  login: async () => {},
  logout: async () => {},
  isSysadmin: false,
  isDev:      false,
  isSenior:   false,
  isJunior:   false,
});

export function useAuth() {
  return useContext(AuthContext);
}

export function useAuthProvider(): AuthContextValue {
  const [firebaseUser, setFirebaseUser] = useState<User | null>(null);
  const [teamUser, setTeamUser]         = useState<TeamUser | null>(null);
  const [loading, setLoading]           = useState(true);

  useEffect(() => {
    const unsub = onAuthChange(async (user) => {
      setFirebaseUser(user);
      if (user) {
        try {
          const tu = await getOrCreateUser({
            uid:         user.uid,
            email:       user.email!,
            displayName: user.displayName || user.email!,
            photoURL:    user.photoURL || undefined,
          });
          setTeamUser(tu);
        } catch (err) {
          console.error('Failed to sync user:', err);
        }
      } else {
        setTeamUser(null);
      }
      setLoading(false);
    });
    return unsub;
  }, []);

  const login = async () => {
    try {
      await signInWithGoogle();
    } catch (err: unknown) {
      const msg = (err as { message?: string })?.message ?? String(err);
      // eslint-disable-next-line no-alert
      alert(`Error al iniciar sesión: ${msg}`);
    }
  };

  const logout = async () => {
    await signOut();
    setTeamUser(null);
  };

  return {
    firebaseUser,
    teamUser,
    loading,
    login,
    logout,
    isSysadmin: teamUser?.trustLevel === 'sysadmin',
    isDev:      ['sysadmin', 'dev'].includes(teamUser?.trustLevel ?? ''),
    isSenior:   ['sysadmin', 'senior'].includes(teamUser?.trustLevel ?? ''),
    isJunior:   teamUser?.trustLevel === 'junior',
  };
}
